import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { MessageSquare, Search, Store, ArrowLeft, Circle } from 'lucide-react';
import { ConversationView } from '@/components/ConversationView';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Vendor {
  id: string;
  full_name: string | null;
  vendor_status: string | null;
  system_role: string | null;
}

interface ConversationSummary {
  otherUserId: string;
  otherUserName: string;
  latestMessage: string;
  latestAt: string;
  unreadCount: number;
  isVendor: boolean;
}

export const AdminMessagingCenter = () => {
  const { user } = useAuth();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<{
    userId: string;
    userName: string;
  } | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'conversations' | 'vendors'>('conversations');

  const fetchVendors = useCallback(async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, vendor_status, system_role')
      .or('system_role.eq.vendor,vendor_status.eq.approved')
      .order('full_name', { ascending: true });
    setVendors((data || []) as Vendor[]);
  }, []);

  const fetchConversations = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data: msgs } = await supabase
        .from('messages')
        .select(`
          id,
          sender_id,
          recipient_id,
          message,
          content,
          subject,
          is_read,
          created_at,
          sender:profiles!sender_id(full_name, system_role, vendor_status),
          recipient:profiles!recipient_id(full_name, system_role, vendor_status)
        `)
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (!msgs) return;

      // Group by the other participant
      const grouped = new Map<string, ConversationSummary>();
      for (const msg of msgs) {
        const isMe = msg.sender_id === user.id;
        const otherId = isMe ? msg.recipient_id : msg.sender_id;
        const otherProfile: any = isMe ? msg.recipient : msg.sender;
        const otherName = otherProfile?.full_name || 'Utilisateur';
        const isVendor =
          otherProfile?.system_role === 'vendor' ||
          otherProfile?.vendor_status === 'approved';

        if (!grouped.has(otherId)) {
          grouped.set(otherId, {
            otherUserId: otherId,
            otherUserName: otherName,
            latestMessage: (msg as any).message || (msg as any).content || '',
            latestAt: msg.created_at,
            unreadCount: 0,
            isVendor,
          });
        }

        const entry = grouped.get(otherId)!;
        if (!isMe && !(msg as any).is_read) {
          entry.unreadCount++;
        }
      }

      setConversations(Array.from(grouped.values()));
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchVendors();
    fetchConversations();
  }, [fetchVendors, fetchConversations]);

  // Realtime: refresh when new message arrives
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel('admin-messaging-center')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        () => fetchConversations()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, fetchConversations]);

  const filteredVendors = vendors.filter(v =>
    (v.full_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const filteredConversations = conversations.filter(c =>
    c.otherUserName.toLowerCase().includes(search.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  // --- Conversation View Panel ---
  if (selectedConversation) {
    return (
      <div className="flex flex-col h-[calc(100vh-220px)] min-h-[500px]">
        <ConversationView
          otherUserId={selectedConversation.userId}
          otherUserName={selectedConversation.userName}
          initialSubject={`Conversation admin ↔ ${selectedConversation.userName}`}
          onBack={() => {
            setSelectedConversation(null);
            fetchConversations();
          }}
          bypassAuth
          richText
        />
      </div>
    );
  }

  // --- Main Panel ---
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-primary" />
            Messagerie Vendeurs
            {totalUnread > 0 && (
              <Badge variant="destructive">{totalUnread} non lu{totalUnread > 1 ? 's' : ''}</Badge>
            )}
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Communiquez directement avec vos vendeurs
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={view === 'conversations' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('conversations')}
          >
            Conversations
          </Button>
          <Button
            variant={view === 'vendors' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('vendors')}
          >
            <Store className="h-4 w-4 mr-1" />
            Tous les vendeurs
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={view === 'vendors' ? 'Rechercher un vendeur…' : 'Rechercher une conversation…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Conversations view */}
      {view === 'conversations' && (
        <div className="space-y-2">
          {loading ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">Chargement…</CardContent>
            </Card>
          ) : filteredConversations.length === 0 ? (
            <Card>
              <CardContent className="py-10 flex flex-col items-center text-center text-muted-foreground gap-3">
                <MessageSquare className="h-10 w-10 opacity-30" />
                <div>
                  <p className="font-medium">Aucune conversation</p>
                  <p className="text-sm">Passez à l'onglet "Tous les vendeurs" pour démarrer une conversation.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            filteredConversations.map(conv => (
              <button
                key={conv.otherUserId}
                onClick={() => setSelectedConversation({ userId: conv.otherUserId, userName: conv.otherUserName })}
                className="w-full text-left p-4 rounded-lg border bg-white hover:bg-muted/30 transition-colors flex items-center gap-3"
              >
                <div className="relative">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {(conv.otherUserName || '?').substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {conv.unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {conv.unreadCount}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm truncate">{conv.otherUserName}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {formatDistanceToNow(new Date(conv.latestAt), { addSuffix: true, locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {conv.isVendor && (
                      <Badge variant="outline" className="text-xs px-1 py-0 shrink-0">🏪 Vendeur</Badge>
                    )}
                    <p className="text-xs text-muted-foreground truncate">
                      {/* Strip HTML tags for preview */}
                      {conv.latestMessage.replace(/<[^>]*>/g, '').slice(0, 80) || '…'}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Vendors list view */}
      {view === 'vendors' && (
        <div className="space-y-2">
          {filteredVendors.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Aucun vendeur trouvé
              </CardContent>
            </Card>
          ) : (
            filteredVendors.map(vendor => {
              const hasConv = conversations.some(c => c.otherUserId === vendor.id);
              const conv = conversations.find(c => c.otherUserId === vendor.id);
              return (
                <button
                  key={vendor.id}
                  onClick={() =>
                    setSelectedConversation({
                      userId: vendor.id,
                      userName: vendor.full_name || 'Vendeur',
                    })
                  }
                  className="w-full text-left p-4 rounded-lg border bg-white hover:bg-muted/30 transition-colors flex items-center gap-3"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-green-100 text-green-700">
                      {(vendor.full_name || '?').substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{vendor.full_name || 'Vendeur'}</span>
                      <Badge variant="outline" className="text-xs px-1 py-0">🏪 Vendeur</Badge>
                      {hasConv && conv && conv.unreadCount > 0 && (
                        <Badge variant="destructive" className="text-xs">{conv.unreadCount}</Badge>
                      )}
                    </div>
                    {hasConv ? (
                      <p className="text-xs text-muted-foreground">Conversation existante</p>
                    ) : (
                      <p className="text-xs text-primary">+ Démarrer une conversation</p>
                    )}
                  </div>
                  <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
